import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { catalogApi, companyApi } from '@/api';
import {
  Alert,
  Badge,
  BoxAvatar,
  BoxList,
  BoxRow,
  CardActions,
  Button,
  EmptyState,
  ErrorState,
  Icon,
  Input,
  ListCard,
  LoadingState,
  PageHeader,
  Pagination,
  SearchInput,
  SegmentedTabs,
  Select,
  ViewButton,
  useToast,
} from '@/components/ui';
import { PlanUpgradeState } from '@/features/billing/PlanUpgradeState';
import { CandidateContactLine } from '@/features/candidates/CandidateContactLine';
import { MessageCandidateButton } from '@/features/messages/MessageCandidateButton';
import { errorMessage, isPlanLimitError } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { cn } from '@/utils/cn';
import { formatDate, humanizeWorkMode } from '@/utils/format';

const SORTS = [
  { value: 'relevance', label: 'Best match' },
  { value: 'completion', label: 'Most complete profile' },
  { value: 'recent', label: 'Newest' },
];

const POOL_TABS = [
  { key: '', label: 'Everyone' },
  { key: 'false', label: 'Not yet saved' },
  { key: 'true', label: 'Saved' },
];

const EMPTY_FILTERS = {
  q: '',
  university_id: '',
  location: '',
  work_mode: '',
  graduation_year_min: '',
  sort: 'recent',
};

const PAGE_SIZE = 20;

/** How many skills a row shows before folding the rest into "+N". */
const SKILLS_SHOWN = 3;

export function CandidateSearchPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [skillIds, setSkillIds] = useState([]);
  const [inPool, setInPool] = useState('');
  const [page, setPage] = useState(1);

  const { data: universities } = useQuery({
    queryKey: ['universities'],
    queryFn: () => catalogApi.listUniversities(),
  });
  const { data: skills } = useQuery({
    queryKey: ['skills'],
    queryFn: () => catalogApi.listSkills(),
  });

  const params = {
    page,
    page_size: PAGE_SIZE,
    sort: filters.sort,
    ...(filters.q.trim() ? { q: filters.q.trim() } : {}),
    ...(filters.university_id ? { university_id: filters.university_id } : {}),
    ...(filters.location.trim() ? { location: filters.location.trim() } : {}),
    ...(filters.work_mode ? { work_mode: filters.work_mode } : {}),
    ...(filters.graduation_year_min
      ? { graduation_year_min: Number(filters.graduation_year_min) }
      : {}),
    ...(inPool ? { in_pool: inPool } : {}),
    ...(skillIds.length ? { skill_ids: skillIds } : {}),
  };

  const { data, isPending, isPlaceholderData, isError, error, refetch } = useQuery({
    queryKey: ['candidate-search', params],
    queryFn: () => companyApi.searchCandidates(params),
    // Keeps the last page on screen while the next one loads, so typing in
    // the search box does not swap the table for a spinner on every key.
    placeholderData: keepPreviousData,
  });

  const save = useMutation({
    mutationFn: (studentId) => companyApi.addToTalentPool({ student_id: studentId }),
    onSuccess: (candidate) => {
      toast.success(`${candidate.full_name} saved to your candidates`);
      void queryClient.invalidateQueries({ queryKey: ['candidate-search'] });
      void queryClient.invalidateQueries({ queryKey: ['talent-pool'] });
    },
    onError: (saveError) => toast.error(errorMessage(saveError, 'Could not save the candidate')),
  });

  function update(field, value) {
    setFilters((current) => ({ ...current, [field]: value }));
    setPage(1);
  }

  function toggleSkill(id) {
    setSkillIds((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
    );
    setPage(1);
  }

  const activeCount = [
    filters.q !== '',
    filters.university_id !== '',
    filters.location !== '',
    filters.work_mode !== '',
    filters.graduation_year_min !== '',
    skillIds.length > 0,
    inPool !== '',
  ].filter(Boolean).length;

  /** Clears what narrows the list; the chosen order is not a filter and stays. */
  function resetFilters() {
    setFilters((current) => ({ ...EMPTY_FILTERS, sort: current.sort }));
    setSkillIds([]);
    setInPool('');
    setPage(1);
  }

  const rows = data?.data ?? [];
  const meta = data?.meta;

  return (
    // On a phone or tablet held upright the list card runs to the bottom of the
    // screen.
    <div className="flex flex-col portrait:flex-1">
      <PageHeader
        title="Find Talent"
        description="Students who marked themselves open to opportunities."
      />

      <Alert tone="info" className="mb-6" title="Reach candidates directly">
        Email or call anyone here, or use Message to write to them on Join-Wonder. Their CV is
        shared once they apply to one of your internships.
      </Alert>

      {/* The profile filters. Search, order and the pool switch live in the
          list's own toolbar; these are too many to fit there. */}
      <section
        aria-label="Refine candidates"
        className="bg-surface border-line rounded-card shadow-card mb-4 border p-4 sm:p-5"
      >
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Select
            label="University"
            size="sm"
            value={filters.university_id}
            onChange={(event) => update('university_id', event.target.value)}
          >
            <option value="">Any university</option>
            {(universities ?? []).map((university) => (
              <option key={university.id} value={university.id}>
                {university.name}
              </option>
            ))}
          </Select>
          <Input
            label="Location"
            size="sm"
            placeholder="Amsterdam"
            value={filters.location}
            onChange={(event) => update('location', event.target.value)}
          />
          <Select
            label="Preferred work mode"
            size="sm"
            value={filters.work_mode}
            onChange={(event) => update('work_mode', event.target.value)}
          >
            <option value="">Any</option>
            <option value="ONSITE">On site</option>
            <option value="HYBRID">Hybrid</option>
            <option value="REMOTE">Remote</option>
          </Select>
          <Input
            label="Graduating from"
            size="sm"
            type="number"
            placeholder="2027"
            value={filters.graduation_year_min}
            onChange={(event) => update('graduation_year_min', event.target.value)}
          />
        </div>

        {(skills ?? []).length > 0 && (
          <div className="border-line mt-4 border-t pt-4">
            <p className="text-ink-muted mb-2 text-xs font-medium">
              Skills — a candidate must have every one you pick
            </p>
            <div className="flex flex-wrap gap-1.5">
              {(skills ?? []).slice(0, 24).map((skill) => {
                const selected = skillIds.includes(skill.id);
                return (
                  <button
                    key={skill.id}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => toggleSkill(skill.id)}
                    className={cn(
                      'inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs transition',
                      selected
                        ? 'border-brand-600 bg-brand-600 font-medium text-white shadow-sm'
                        : 'border-line bg-surface text-ink-muted hover:border-brand-200 hover:bg-brand-50/60 hover:text-ink',
                    )}
                  >
                    {selected && <Icon name="check" size={12} />}
                    {skill.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </section>

      <ListCard
        fill
        className="flex-1"
        isRefreshing={isPlaceholderData}
        toolbarStart={
          <SegmentedTabs
            variant="plain"
            label="Saved candidates"
            tabs={POOL_TABS}
            value={inPool}
            onChange={(key) => {
              setInPool(key);
              setPage(1);
            }}
          />
        }
        toolbarEnd={
          <>
            <Select
              label="Sort by"
              hideLabel
              size="sm"
              wrapperClassName="w-full sm:w-44"
              value={filters.sort}
              onChange={(event) => update('sort', event.target.value)}
            >
              {SORTS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <SearchInput
              label="Search"
              hideLabel
              size="sm"
              className="w-full sm:w-64"
              placeholder="Name, headline or field of study"
              value={filters.q}
              onChange={(event) => update('q', event.target.value)}
              onClear={() => update('q', '')}
            />
            {activeCount > 0 && (
              <Button variant="ghost" size="sm" onClick={resetFilters}>
                <Icon name="reset" size={14} />
                Reset
              </Button>
            )}
          </>
        }
        state={
          isError ? (
            isPlanLimitError(error) ? (
              <PlanUpgradeState error={error} />
            ) : (
              <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
            )
          ) : isPending ? (
            <LoadingState label="Searching candidates…" />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<Icon name="search" size={22} />}
              title="No candidates match"
              description="Widen the filters, or check back as more students complete their profile."
              action={
                activeCount > 0 && (
                  <Button variant="secondary" size="sm" onClick={resetFilters}>
                    Reset filters
                  </Button>
                )
              }
            />
          ) : null
        }
        footer={
          meta && (
            <Pagination
              className=""
              page={meta.page}
              pageSize={PAGE_SIZE}
              totalPages={meta.total_pages}
              totalItems={meta.total_items}
              onPageChange={setPage}
            />
          )
        }
      >
        <BoxList>
          {rows.map((candidate) => (
            <BoxRow
              key={candidate.id}
              accent={candidateAccent(candidate)}
              leading={<BoxAvatar name={candidate.full_name} round />}
              title={candidate.full_name}
              to={paths.company.candidateDetail(candidate.id)}
              badges={<CandidateBadges candidate={candidate} />}
              subtitle={candidate.headline}
              facts={candidateFacts(candidate)}
              // Top right, as a card keeps its bookmark. Filled once saved; there
              // is no unsave here (that is on the talent pool), so it stays put.
              corner={
                <Button
                  variant="ghostBrand"
                  size="sm"
                  title={
                    candidate.pool ? 'In your pool' : `Save ${candidate.full_name} to your pool`
                  }
                  aria-pressed={Boolean(candidate.pool)}
                  disabled={Boolean(candidate.pool)}
                  isLoading={save.isPending && save.variables === candidate.id}
                  onClick={() => save.mutate(candidate.id)}
                >
                  <Icon name="bookmark" size={14} filled={Boolean(candidate.pool)} />
                  {candidate.pool ? 'Saved' : 'Save'}
                </Button>
              }
              actions={
                <CardActions>
                  <MessageCandidateButton candidate={candidate} />
                  <ViewButton
                    to={paths.company.candidateDetail(candidate.id)}
                    title={`View ${candidate.full_name}'s profile`}
                    label="View profile"
                  />
                </CardActions>
              }
            >
              <CandidateContactLine contact={candidate.contact} />
              <SkillBadges skills={candidate.top_skills} />
            </BoxRow>
          ))}
        </BoxList>
      </ListCard>
    </div>
  );
}

/** Saved candidates read green, ones who applied to you violet, the rest grey. */
function candidateAccent(candidate) {
  if (candidate.pool) return 'success';
  if (candidate.application_count > 0) return 'brand';
  return 'neutral';
}

function CandidateBadges({ candidate }) {
  return (
    <>
      {candidate.application_count > 0 && (
        <Badge tone="brand">
          {candidate.application_count === 1
            ? 'Applied to you'
            : `${candidate.application_count} applications`}
        </Badge>
      )}
      {candidate.pool && <Badge tone="success">Saved</Badge>}
    </>
  );
}

/** Degree and university, location and availability, and how complete the profile is. */
function candidateFacts(candidate) {
  const study = [candidate.degree, candidate.university].filter(Boolean).join(' · ');
  return [
    study && { icon: 'graduationCap', text: study },
    candidate.graduation_year && {
      icon: 'calendar',
      text: `Class of ${candidate.graduation_year}`,
    },
    candidate.location && { icon: 'home', text: candidate.location },
    candidate.preferred_work_mode && {
      icon: 'briefcase',
      text: humanizeWorkMode(candidate.preferred_work_mode),
    },
    candidate.available_from && {
      icon: 'calendarClock',
      text: `Available ${formatDate(candidate.available_from)}`,
    },
    {
      icon: 'userCheck',
      text: `${candidate.profile_completion ?? 0}% profile`,
      tone: (candidate.profile_completion ?? 0) >= 80 ? 'success' : undefined,
    },
  ];
}

function SkillBadges({ skills }) {
  if (!skills?.length) return null;
  const hidden = skills.length - SKILLS_SHOWN;
  return (
    <div className="mt-2.5 flex flex-wrap gap-1">
      {skills.slice(0, SKILLS_SHOWN).map((skill) => (
        <Badge key={skill} tone="brand">
          {skill}
        </Badge>
      ))}
      {hidden > 0 && (
        <Badge tone="neutral" title={skills.slice(SKILLS_SHOWN).join(', ')}>
          +{hidden}
        </Badge>
      )}
    </div>
  );
}
